// Server-only reads for the public feedback page.
//
// Mirrors src/lib/training.ts: a `createServerFn` loader so the form is server
// rendered with its questions already in the HTML, instead of the page booting
// empty and fetching itself.

import { notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { prisma } from "#/db";
import type { Dict, Locale } from "#/lib/i18n";
import {
	parseContent,
	parseQuestions,
	type Question,
	type QuestionnaireContent,
} from "#/lib/questionnaire";

export interface PublicQuestionnaire {
	trainingName: string;
	trainingSlug: string;
	/** Per-locale landing meta of the parent training — used for the page title. */
	trainingMeta: Record<Locale, { title: string }> | null;
	/** The run this form belongs to — null only when the training never ran. */
	editionNumber: number | null;
	/**
	 * Null when the edition has no questionnaire at all, or has one that is
	 * switched off. Both render the same "closed" screen: which of the two it is
	 * tells a visitor nothing useful, and a 404 would read as a broken link.
	 */
	questionnaire: {
		id: string;
		collectIdentity: boolean;
		content: QuestionnaireContent;
		questions: Question[];
	} | null;
}

export const getPublicQuestionnaire = createServerFn({ method: "GET" })
	.validator((input: { slug: string; edition?: number }) => input)
	.handler(async ({ data: input }): Promise<PublicQuestionnaire> => {
		const training = await prisma.training.findUnique({
			where: { slug: input.slug },
			select: {
				name: true,
				slug: true,
				content: true,
				editions: {
					select: {
						editionNumber: true,
						questionnaire: {
							select: {
								id: true,
								isActive: true,
								collectIdentity: true,
								content: true,
								questions: true,
							},
						},
					},
					orderBy: { editionNumber: "desc" },
				},
			},
		});

		// The training itself not existing *is* a broken link.
		if (!training) throw notFound();

		const content = training.content as Record<Locale, Dict> | null;
		const trainingMeta = content
			? ({
					ar: { title: content.ar?.meta?.title ?? training.name },
					fr: { title: content.fr?.meta?.title ?? training.name },
					en: { title: content.en?.meta?.title ?? training.name },
				} satisfies Record<Locale, { title: string }>)
			: null;

		let edition: (typeof training.editions)[number] | undefined;
		if (input.edition === undefined) {
			// A link without an edition was handed out before editions existed, or
			// as "the training's form": it goes to the newest run whose form is
			// open, and otherwise to the newest run (which shows as closed).
			edition =
				training.editions.find(
					(e) =>
						e.questionnaire?.isActive &&
						parseQuestions(e.questionnaire.questions).length > 0,
				) ?? training.editions[0];
		} else {
			edition = training.editions.find(
				(e) => e.editionNumber === input.edition,
			);
			// An edition that never existed is a broken link, not a closed form.
			if (!edition) throw notFound();
		}

		const row = edition?.questionnaire;
		const questions = row ? parseQuestions(row.questions) : [];

		return {
			trainingName: training.name,
			trainingSlug: training.slug,
			trainingMeta,
			editionNumber: edition?.editionNumber ?? null,
			questionnaire:
				row?.isActive && questions.length
					? {
							id: row.id,
							collectIdentity: row.collectIdentity,
							content: parseContent(row.content),
							questions,
						}
					: null,
		};
	});
